'use client'

import { useQuery } from '@tanstack/react-query'
import React from 'react'

import { getPublicEnv } from '@/shared/env'
import type { Post } from '@/shared/newsdb'
import type { Language } from '@/shared/providers'

import { getPostTranslation } from './action'

export function useTranslation(post: Post) {
    const runtimeEnv = getPublicEnv()

    const [translationState, setTranslationState] = React.useState<{ lang: Language; translated: boolean }>({
        lang: runtimeEnv.NEXT_PUBLIC_LANG,
        translated: false,
    })

    const { data } = useQuery({
        queryKey: ['post.translation', post.id, translationState.lang],
        queryFn: () => getPostTranslation(post.id, translationState.lang),
        enabled: translationState.translated,
        placeholderData: {
            title: post.title,
            content: post.content,
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    })

    const handleTranslateButtonClick = (langToTranslate: Language) => {
        setTranslationState({
            lang: langToTranslate,
            translated: langToTranslate !== runtimeEnv.NEXT_PUBLIC_LANG,
        })
    }

    // When auto-translation is off, the post stays in its original language, so the date follows CONTENT_LANG too.
    const lang = runtimeEnv.NEXT_PUBLIC_AUTO_TRANSLATION_ENABLE ? translationState.lang : runtimeEnv.NEXT_PUBLIC_LANG

    return { data, handleTranslateButtonClick, isTranslated: translationState.translated, lang } as const
}
